/**
 * #1184 — read-only account record for old Kalshi activity.
 *
 * Recorded history, visibly not a market: no order, cancel, arm, size or bot
 * control, no ticket, no feed demand. The only actions are Close and — only
 * while the live store resolves the game (`useGameDestination`) — a link to
 * Game Detail. The `aria-modal` root pauses hotkeys while it is open
 * (`Lt2HotkeyListener`'s MODAL_SELECTOR), so a keypress cannot reach the
 * active ticket from here; the hotkey aim itself is never touched.
 */
import { createContext, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Modal } from '@/components/atoms/Modal';
import { MarketTypeChip, Pill, SideBadge } from '@/components/cards/cardKit';
import { describeMarket } from '@/lib/kalshiTicker';
import { useAccountIdentityVersion } from '@/stores/accountMarketIdentity';
import type { AccountRecordVM } from './accountRecordModel';
import { useGameDestination, type GameDestination } from './gameDestination';

const NOT_IN_RECORD = 'Not in this record';

/** False in a window that cannot usefully receive Game Detail — the 420×640
 *  `/fills` popup. Navigating it would load a second live trading page (its own
 *  book demand and ticket) in a chrome-less window, so the record names the
 *  live game and points to the main window instead of linking. */
export const RecordGameLinksContext = createContext(true);

export function AccountRecordDialog({ record, onClose }: { record: AccountRecordVM | null; onClose: () => void }) {
  if (!record) return null;
  // Portaled to <body>: the opener lives in a dock/rail whose ancestors may
  // clip or form a containing block for `position: fixed` (a backdrop-filter
  // or transform does), which would trap the overlay inside a 384px rail.
  return createPortal(<RecordBody record={record} onClose={onClose} />, document.body);
}

/** CSS scroll shadows (background-attachment: local covers them at the ends):
 *  the fade appears only while the label overflows its box, in both themes. */
const LABEL_SCROLL_CUE = {
  background: [
    'linear-gradient(hsl(var(--card)) 30%, transparent) top / 100% 12px no-repeat local',
    'linear-gradient(transparent, hsl(var(--card)) 70%) bottom / 100% 12px no-repeat local',
    'linear-gradient(hsl(var(--muted-foreground) / 0.25), transparent) top / 100% 6px no-repeat scroll',
    'linear-gradient(transparent, hsl(var(--muted-foreground) / 0.25)) bottom / 100% 6px no-repeat scroll',
  ].join(', '),
};

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keeps keyboard focus inside the record while it is open. Without it a click
 * on the title drops focus to <body>, where Lt2HotkeyListener treats Escape as
 * the board's and clears the hotkey aim (its Escape branch runs before the
 * modal gate), and Tab walks out to controls behind the backdrop. The shared
 * Modal has the same gap on main for every use; this closes it for the record
 * only (#1184 review — Sheila chose record-scoped over a listener change).
 */
function useRecordFocusContainment(body: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = body.current;
    const root = el?.closest<HTMLElement>('[data-modal-root]');
    if (!el || !root) return;
    el.focus({ preventScroll: true });
    const onFocusIn = (e: FocusEvent) => {
      if (!root.contains(e.target as Node)) el.focus({ preventScroll: true });
    };
    // A click on non-focusable record text blurs to <body>; take focus back
    // (a focus() call keeps any text selection, so the ticker stays copyable).
    const onPointerUp = (e: PointerEvent) => {
      if (root.contains(e.target as Node) && !root.contains(document.activeElement)) el.focus({ preventScroll: true });
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const stops = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (stops.length === 0) { e.preventDefault(); el.focus({ preventScroll: true }); return; }
      const first = stops[0], last = stops[stops.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === el || !root.contains(active))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (active === last || !root.contains(active))) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('pointerup', onPointerUp, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('pointerup', onPointerUp, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [body]);
}

function RecordBody({ record, onClose }: { record: AccountRecordVM; onClose: () => void }) {
  // Saved metadata can land while the record is open: repaint the label in place.
  useAccountIdentityVersion((s) => s.version);
  const { marketLabel, betTeam, badgeTeam, marketType } = describeMarket(record.ticker, record.side);
  const destination = useGameDestination(record.candidateHref, record.ticker);
  // Focus moves into the record and stays there; it returns to the opener on
  // close (useAccountRecord).
  const body = useRef<HTMLDivElement>(null);
  useRecordFocusContainment(body);
  // No live-update reflow (#1184): the window is pinned near the top instead of
  // centered, the badge row never wraps, and the label — which can arrive late
  // and run to hundreds of characters — has a fixed 3-line box that scrolls
  // with a visible cue. Nothing above the facts changes height, so nothing moves.
  return (
    <Modal isOpen onClose={onClose} title="Account record" description="Recorded history — not a live market" size="md"
      className="mx-4 mt-[10vh] self-start">
      <div ref={body} tabIndex={-1} data-testid="account-record" data-ticker={record.ticker} className="space-y-3 text-sm outline-none">
        <div className="flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden">
          <Pill className="bg-muted text-foreground shrink-0">{record.actionLabel}</Pill>
          <SideBadge side={record.side} team={badgeTeam ?? betTeam} />
          <MarketTypeChip marketType={marketType} />
        </div>
        <p data-testid="account-record-label" tabIndex={0} aria-label={`Market: ${marketLabel}`}
          className="text-foreground h-[3.75rem] overflow-y-auto font-medium leading-5 [overflow-wrap:anywhere]"
          style={LABEL_SCROLL_CUE}>
          {marketLabel}
        </p>
        <p className="text-muted-foreground break-all font-mono text-xs" title="Exact ticker">{record.ticker}</p>
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1">
          {record.facts.map((fact) => (
            <div key={fact.label} className="contents">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className={`font-mono [overflow-wrap:anywhere] ${fact.value == null ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                {fact.value ?? NOT_IN_RECORD}
              </dd>
            </div>
          ))}
        </dl>
        <DestinationLine destination={destination} onNavigate={onClose} />
      </div>
    </Modal>
  );
}

/** Never "closed", "retired" or "dead": a game missing from the loaded rows
 *  may simply not have arrived yet. */
function DestinationLine({ destination, onNavigate }: { destination: GameDestination; onNavigate: () => void }) {
  const links = useContext(RecordGameLinksContext);
  return (
    <div role="status" className="border-border text-muted-foreground border-t pt-3 text-xs">
      {destination.kind === 'absent' ? (
        <p>Not on the current game board.</p>
      ) : (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>
            {destination.kind === 'live' ? 'Game is on the live board.' : 'Market details are available.'}
            {destination.kind === 'live' && !destination.lineInFeed && " This exact line isn't in the live feed."}
          </span>
          {links ? (
            <Link to={destination.href} onClick={onNavigate} className="text-primary underline">
              {destination.kind === 'live' ? 'Open game' : 'Open market details'}
            </Link>
          ) : (
            <span>Open it from the main g1 window.</span>
          )}
        </p>
      )}
    </div>
  );
}
